// lib/pin.ts — PIN key derivation + WebAuthn biometric integration

import { encryptData, decryptData, generateSalt, generateMasterKey, exportKey, importKey, toBase64, fromBase64 } from './crypto';
import type { PinState } from '../types';

const PBKDF2_ITERATIONS_PIN = 120_000;

/** Derive an AES-GCM key from a PIN + UID (used as salt component) */
async function derivePinKey(pin: string, uid: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const combined = enc.encode(`${uid}:${pin}`);
  const baseKey = await crypto.subtle.importKey('raw', combined, 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS_PIN, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/** Derive an AES-GCM key from a 12-word recovery mnemonic + UID */
async function deriveRecoveryKey(mnemonic: string, uid: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const cleanMnemonic = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ');
  const combined = enc.encode(`${uid}:${cleanMnemonic}`);
  const baseKey = await crypto.subtle.importKey('raw', combined, 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: PBKDF2_ITERATIONS_PIN, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Set up PIN: generates master key, encrypts it with PIN, and optionally with a 12-word recovery code.
 */
export async function setupPin(
  pin: string,
  uid: string,
  recoveryCode?: string
): Promise<{ pinState: PinState; masterKey: CryptoKey }> {
  const masterKey = await generateMasterKey();

  const salt = generateSalt();
  const pinKey = await derivePinKey(pin, uid, salt);
  const masterKeyBytes = await exportKey(masterKey);
  const encrypted = await encryptData(Array.from(masterKeyBytes), pinKey);

  const pinState: PinState = {
    encryptedMasterKey: encrypted,
    iv: '', // IV is already embedded in encryptData output
    salt: toBase64(salt),
  };

  if (recoveryCode) {
    const saltBackup = generateSalt();
    const recoveryKey = await deriveRecoveryKey(recoveryCode, uid, saltBackup);
    const encryptedBackup = await encryptData(Array.from(masterKeyBytes), recoveryKey);
    pinState.encryptedMasterKeyBackup = encryptedBackup;
    pinState.saltBackup = toBase64(saltBackup);
  }

  return { pinState, masterKey };
}

/**
 * Set up PIN using an existing master key (re-encrypts it).
 */
export async function setupPinWithExistingKey(
  pin: string,
  uid: string,
  masterKey: CryptoKey,
  recoveryCode?: string
): Promise<PinState> {
  const salt = generateSalt();
  const pinKey = await derivePinKey(pin, uid, salt);
  const masterKeyBytes = await exportKey(masterKey);
  const encrypted = await encryptData(Array.from(masterKeyBytes), pinKey);

  const pinState: PinState = {
    encryptedMasterKey: encrypted,
    iv: '',
    salt: toBase64(salt),
  };

  if (recoveryCode) {
    const saltBackup = generateSalt();
    const recoveryKey = await deriveRecoveryKey(recoveryCode, uid, saltBackup);
    const encryptedBackup = await encryptData(Array.from(masterKeyBytes), recoveryKey);
    pinState.encryptedMasterKeyBackup = encryptedBackup;
    pinState.saltBackup = toBase64(saltBackup);
  }

  return pinState;
}

/**
 * Unlock master key using PIN.
 * Throws if PIN is incorrect.
 */
export async function unlockWithPin(pin: string, uid: string, pinState: PinState): Promise<CryptoKey> {
  const salt = fromBase64(pinState.salt);
  const pinKey = await derivePinKey(pin, uid, salt);
  const masterKeyArray = await decryptData<number[]>(pinState.encryptedMasterKey, pinKey);
  return importKey(new Uint8Array(masterKeyArray));
}

/**
 * Unlock master key using a 12-word recovery code backup.
 */
export async function unlockWithRecoveryCode(
  recoveryCode: string,
  uid: string,
  pinState: PinState
): Promise<CryptoKey> {
  if (!pinState.encryptedMasterKeyBackup || !pinState.saltBackup) {
    throw new Error('No recovery backup available');
  }
  const saltBackup = fromBase64(pinState.saltBackup);
  const recoveryKey = await deriveRecoveryKey(recoveryCode, uid, saltBackup);
  const masterKeyArray = await decryptData<number[]>(pinState.encryptedMasterKeyBackup, recoveryKey);
  return importKey(new Uint8Array(masterKeyArray));
}

// ---- WebAuthn Biometric ----

/**
 * Check if the browser supports platform authenticators (biometrics).
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Enroll biometric: creates a WebAuthn credential and stores credentialId + wrapped key.
 * The master key is encrypted under a challenge-derived secret stored as a user handle.
 * For simplicity in beta: we store the encrypted master key bytes in the user handle field
 * and retrieve the credentialId to use as lookup key in IndexedDB.
 */
export async function enrollBiometric(uid: string, masterKey: CryptoKey): Promise<string> {
  console.debug('Enrolling biometric for master key type:', masterKey.type);

  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const masterKeyBytes = await exportKey(masterKey);

  const credential = await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: 'sortednow', id: window.location.hostname },
      user: {
        id: masterKeyBytes as BufferSource,
        name: uid,
        displayName: 'sortednow',
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },  // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60000,
    },
  }) as PublicKeyCredential | null;

  if (!credential) throw new Error('Biometric enrollment cancelled');

  const credentialId = toBase64(new Uint8Array(credential.rawId));
  return credentialId;
}

/**
 * Verify biometric using stored credentialId.
 * Returns the raw master key bytes if biometric passes, or null otherwise.
 */
export async function verifyBiometric(credentialId: string): Promise<Uint8Array | null> {
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const credIdBytes = fromBase64(credentialId);

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{ type: 'public-key', id: credIdBytes as BufferSource }],
        userVerification: 'required',
        timeout: 60000,
      },
    }) as PublicKeyCredential | null;

    if (!assertion) return null;
    const response = assertion.response as AuthenticatorAssertionResponse;
    if (!response.userHandle) return null;
    return new Uint8Array(response.userHandle);
  } catch {
    return null;
  }
}
